<template>
  <LcCard
    body-class="flex pt-100"
    :class="{
      'toggle-card': true,
      'toggle-card-disabled': disabled,
    }"
    header-class="toggle-card-header flex justify-between items-center"
  >
    <div
      :class="['toggle-card-backdrop', `toggle-card-backdrop-${model ? 'active' : 'inactive'}`]"
    />

    <template #header>
      <div class="flex items-center">
        <div class="toggle-card-image mr-100">
          <LcImage v-bind="inactiveImage!" alt="" />
          <Transition name="fade">
            <LcImage
              v-show="model"
              v-bind="activeImage!"
              alt=""
              class="toggle-card-image-inactive"
            />
          </Transition>
        </div>

        <span class="toggle-card-headline font-medium headline-h4-headline">
          <slot name="headline" />
        </span>
        <LcInfoButton
          v-bind="{ disabled }"
          :key="`infobutton_${locale}`"
          class="ml-100"
          :headline="infoHeadline"
          :open-button-aria-label="infoButtonAriaLabel"
          :text="infoText"
        />
      </div>
      <LcForm :tracking="{ containerId }">
        <LcFormField v-slot="{ field }" v-bind="{ name }">
          <LcSwitch
            v-bind="field"
            v-model:value="model"
            :aria-label-text="switchAriaLabelText"
            :disabled="disabled"
          />
        </LcFormField>
      </LcForm>
    </template>

    <div
      class="flex items-center justify-between elevate caption-regular text-secondary-secondary w-full"
    >
      <slot />
    </div>
  </LcCard>
</template>

<script lang="ts" setup>
import type { ToggleCardVariantStyle } from '~/components/Toggle/types'

type ToggleCardProps = {
  containerId: string
  disabled?: boolean
  infoButtonAriaLabel: string
  infoHeadline: string
  infoText: string
  name: string
  switchAriaLabelText: string
  variant?: ToggleCardVariantStyle
}

const {
  disabled = false,
  variant = {
    gradient: {
      bottom: { color: '#FFF7D099', stop: '101%' },
      top: { color: '#FFD921', stop: '0%' },
    },
    illustration: {
      active: 'rocket-active',
      inactive: 'rocket',
    },
    opacity: { active: 0.4, inactive: 0.2 },
  },
} = defineProps<ToggleCardProps>()

const { getIllustrationMini } = useAsset()
const { locale } = useI18n()

const model = defineModel({ default: false, type: Boolean })

const activeImage = computed(() => {
  const illustration = variant.illustration
  return getIllustrationMini(illustration.active)
})
const inactiveImage = computed(() => {
  const illustration = variant.illustration
  return getIllustrationMini(illustration.inactive)
})
</script>

<style lang="scss" scoped>
$base: 1;
$elevate: 2;
$size: 31.75rem;
$transition: 120ms ease-out;
$min-right: -12.5rem;
$max-right: -22rem;

@mixin transparency($opacity) {
  background-color: rgba(255, 255, 255, $opacity);
  transition: background-color $transition;
}

.toggle-card {
  overflow: clip;

  &-headline {
    overflow-wrap: break-word;
  }

  &-image {
    position: relative;
    min-width: calc(v-bind('activeImage?.width') * 1px);

    &-inactive {
      position: absolute;
      top: 0;
      left: 0;
    }
  }
}

.toggle-card {
  .toggle-card-backdrop {
    @include transition-default(opacity);

    position: absolute;
    top: -12.5rem;
    right: clamp($max-right, calc($min-right + $max-right + 50vw), $min-right);
    z-index: $base;
    width: $size;
    height: $size;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      v-bind('variant.gradient.top.color') v-bind('variant.gradient.top.stop'),
      v-bind('variant.gradient.bottom.color') v-bind('variant.gradient.bottom.stop')
    );
    opacity: v-bind('variant.opacity.active');

    &-inactive {
      opacity: v-bind('variant.opacity.inactive');
    }
  }
}

.toggle-card-disabled {
  background-color: rgb(token('color/background/secondary/secondary/disabled'), 60%);
  pointer-events: none;

  :deep(.toggle-card-header) {
    opacity: 0.6;
  }
}

:deep(.toggle-card-header) {
  position: relative;
  z-index: $elevate;
}

.elevate {
  z-index: $elevate;
}

.fade-enter-active,
.fade-leave-active {
  transition: $transition;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
